import { useEffect, useRef, useState } from 'preact/hooks';

import { getIsolatedKeyFromWidgetName } from 'Addons/fitmentSearch/isolatedKeys';
import {
  DialogVehicleWidgetName,
  HeaderVehicleWidgetName,
  TabsVehicleWidgetName,
  VehicleWidgetDialogName,
} from 'Components/names';
import type { FacetToggleConfig } from 'Components/smallComponents/facetToggle';
import type { TemplateFunction, TemplateFunctionInvoker, TemplateResult } from 'Components/types';
import { dialogOpened } from 'Core/actions/dialog';
import {
  createFitmentSearchResponseFacetsSelector,
  createFitmentSearchResponseSelectionSelector,
  createIsServerChangedVehicleSelector,
  isVehicleSelectedSelector,
  isVehicleSelectionForcedSelector,
  selectedVehicleSelector,
} from 'Core/selectors/fitmentSearch/index';
import { nonVehicleMessageSelector, nonVehicleSpecificMessageSelector } from 'Core/selectors/search';
import { useDispatch, useSelector } from 'Core/store';
import uiConfig from 'Models/uiConfig/uiConfig';

import AutoSyncVisualization, {
  type AutoSyncConfig,
  type Params as AutoSyncVisualizationParams,
  type Props as AutoSyncVisualizationProps,
} from '../autoSyncVisualization';
import type { VehicleDropdownsTemplateParams } from '../garage/dropdown/vehicleDropdowns';
import { getClassesByLayoutState, useLayoutState } from './useLayoutState';
import VehicleDropdowns from './vehicleDropdowns';
import VehicleLabel, { type VehicleLabelTemplateParams } from './vehicleLabel';

const hiddenClass = 'cm_hide';

interface Props {
  template: TemplateFunction<VehicleWidgetTemplateParams>;
  isAlwaysActive?: boolean;
  isAlwaysColumnLayout?: boolean;
  columnBreakpoint?: number;
  fields?: string[];
  initCollapsed?: boolean;
  name: string;
  onSelectVehicle?: () => void;
  redirectUrl?: string;
  isHidden?: boolean;
  isVehicleSelectionIsolated?: boolean;
  isAutoVehicleSelectionDisabled?: boolean;
  doNotRedirectOnVehicleSelect?: boolean;
  useNativeDropdown?: boolean;
  useSearchableDropdown?: boolean;
  autoOpenDropdowns?: 'all' | 'next';
  globalVehicleDiscardEnabled?: boolean;
  facetToggleConfig?: FacetToggleConfig;
  useAutoSyncVisualizer?: boolean;
  autoSyncConfig?: AutoSyncConfig;
}

export type VehicleWidgetTemplateParams = VehicleDropdownsTemplateParams | VehicleLabelTemplateParams;

export default function VehicleWidget({
  template,
  isAlwaysActive,
  isAlwaysColumnLayout = false,
  isVehicleSelectionIsolated,
  columnBreakpoint,
  fields,
  initCollapsed = false,
  name,
  onSelectVehicle: onSelectVehicleExternal,
  redirectUrl,
  isHidden: isHiddenProp,
  isAutoVehicleSelectionDisabled,
  doNotRedirectOnVehicleSelect,
  useNativeDropdown,
  useSearchableDropdown,
  autoOpenDropdowns,
  globalVehicleDiscardEnabled,
  facetToggleConfig,
  useAutoSyncVisualizer,
  autoSyncConfig = {},
}: Props): TemplateResult {
  const rootRef = useRef<HTMLElement>(null);
  const dispatch = useDispatch();

  const selectedVehicle = useSelector(selectedVehicleSelector);
  const isVehicleSelectionForced = useSelector(isVehicleSelectionForcedSelector);
  const nonVehicleSpecificResults = useSelector(nonVehicleSpecificMessageSelector);
  const nonVehicleResults = useSelector(nonVehicleMessageSelector);

  const [aboutToChange, setAboutToChange] = useState(false);

  const isolatedKey = getIsolatedKeyFromWidgetName(name);
  const [nameRoot] = name.split('_');

  const isHidden = useIsHidden(
    nameRoot,
    isolatedKey,
    isHiddenProp,
    nonVehicleSpecificResults,
    nonVehicleResults,
  );

  const responseFacets = useSelector(createFitmentSearchResponseFacetsSelector(isolatedKey));
  const layoutState = useLayoutState(rootRef, responseFacets, columnBreakpoint, isAlwaysColumnLayout, fields);
  const templateName = useTemplateName(isAlwaysActive || aboutToChange, isolatedKey, fields);

  useEffect(
    function showLabelWhenVehicleChanges() {
      setAboutToChange(false);
    },
    [selectedVehicle],
  );

  const onChangeVehicle = () => setAboutToChange(true);
  const onSelectVehicle = () => {
    onSelectVehicleExternal?.();
    setAboutToChange(false);
  };
  const openDialog = () => dispatch(dialogOpened(VehicleWidgetDialogName, { withCurrentVehicle: true }));
  const autoSyncVisualization: TemplateFunctionInvoker<AutoSyncVisualizationParams> | undefined = (
    templ: TemplateFunction<AutoSyncVisualizationParams>,
  ) => {
    if (!useAutoSyncVisualizer) {
      return null;
    }

    const props = {
      template: templ,
      config: autoSyncConfig,
    } satisfies AutoSyncVisualizationProps;

    return <AutoSyncVisualization {...props} key="autosync-visualization" />;
  };

  if (templateName === null) {
    return null;
  }

  const appendedClasses = [...getClassesByLayoutState(layoutState), isHidden ? hiddenClass : ''];
  const sharedProps = {
    template,
    isolatedKey,
    onChangeVehicle,
    openDialog,
    rootRef,
  };

  if (templateName === 'active') {
    return (
      <VehicleDropdowns
        {...sharedProps}
        name={name}
        fields={fields}
        initCollapsed={initCollapsed}
        aboutToChange={aboutToChange}
        isAlwaysActive={isAlwaysActive}
        onSelectVehicle={onSelectVehicle}
        layoutState={layoutState}
        isVehicleSelectionIsolated={isVehicleSelectionIsolated}
        useNativeDropdown={useNativeDropdown}
        facetToggleConfig={facetToggleConfig}
        useSearchableDropdown={useSearchableDropdown}
        autoOpenDropdowns={autoOpenDropdowns}
        globalVehicleDiscardEnabled={globalVehicleDiscardEnabled}
        redirectUrl={
          nameRoot === HeaderVehicleWidgetName && nonVehicleResults ? uiConfig.searchPageUrl : redirectUrl
        }
        isAutoVehicleSelectionDisabled={isAutoVehicleSelectionDisabled}
        doNotRedirectOnVehicleSelect={doNotRedirectOnVehicleSelect}
        isVehicleSelectionForced={isVehicleSelectionForced}
        appendedClasses={appendedClasses}
        autoSyncVisualization={autoSyncVisualization}
      />
    );
  }

  return (
    <VehicleLabel
      {...sharedProps}
      appendedClasses={appendedClasses}
      facetToggleConfig={facetToggleConfig}
      globalVehicleDiscardEnabled={globalVehicleDiscardEnabled}
      templateName={templateName}
      autoSyncVisualization={autoSyncVisualization}
    />
  );
}

function useIsHidden(
  nameRoot: string,
  isolatedKey: string | null,
  isHidden: boolean | undefined,
  nonVehicleSpecificResults: boolean,
  fullyNonVehicleResults: boolean,
): boolean {
  return useSelector((state) => {
    const isAnyFitmentFacetSelected =
      !!createFitmentSearchResponseSelectionSelector(isolatedKey)(state)?.length;

    return (
      isHidden ||
      (nameRoot !== HeaderVehicleWidgetName &&
        nameRoot !== DialogVehicleWidgetName &&
        nameRoot !== TabsVehicleWidgetName &&
        ((nonVehicleSpecificResults && !isAnyFitmentFacetSelected) || fullyNonVehicleResults))
    );
  });
}

function useTemplateName(
  forceDropdowns: boolean,
  isolatedKey: string | null,
  fields: string[] | undefined,
): string {
  return useSelector(
    forceDropdowns
      ? () => 'active'
      : (state) =>
          !isVehicleSelectedSelector(state) ||
          createIsServerChangedVehicleSelector(isolatedKey)(state, fields)
            ? 'active'
            : 'locked',
  );
}
